import React, { useState, useEffect } from 'react';
import {
    Users, Search, Phone, Mail, UserCheck, Shield, LogOut, ChevronDown,
    RefreshCw, Loader2, Building2, Wallet, Plus, CheckSquare, Edit3, tool
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import Dropdown from '../components/ui/Dropdown';
import {
    fetchEmployeeList,
    fetchStoresWhereStaffAssigned,
    fetchBankList,           // API helper to get list of bank options
    verifyBankAccount,       // Proxy verification API
    saveEmployeeBankAccount,  // API to attach bank account to employee
    client,
    currencyCodeToSymbolMap,
    fetchTaxCategoriesWithRules,
} from '../apiHelper';
import { useToast } from '../hooks/useToast';
import { useNavigate } from 'react-router-dom';

export const EmployeeProfilesManager = ({ storeId }) => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');

    // --- Modal & Form State ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [resolvedStoreId, setResolvedStoreId] = useState(storeId || null);
    const [bankOptions, setBankOptions] = useState([]);

    const [bankCode, setBankCode] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [saving, setSaving] = useState(false);
    const [verificationData, setVerificationData] = useState(null);

    // --- Salary Modal State ---
    const [isSalaryModalOpen, setIsSalaryModalOpen] = useState(false);
    const [salaryForm, setSalaryForm] = useState({
        basic_salary: '',
        housing_allowance: '',
        transport_allowance: '',
        other_allowances: '',
        effective_date: new Date().toISOString().split('T')[0],
        effective_until: '',
        is_active: true
    });
    const [savingSalary, setSavingSalary] = useState(false);
    const activeStore = stores.find(s => Number(s.store_id) === Number(resolvedStoreId));
    const activeCurrencyCode = activeStore?.setccurr || 'NGN';
    const activeCurrencySymbol = currencyCodeToSymbolMap[activeCurrencyCode] || 'NGN';

    const [taxCategories, setTaxCategories] = useState([]);
    useEffect(() => {
        const loadTaxCategories = async () => {
            try {
                const categories = await fetchTaxCategoriesWithRules();
                // console.log(categories)
                setTaxCategories(Array.isArray(categories) ? categories : []);
            } catch (err) {
                console.error("Failed to load tax categories:", err);
            }
        };
        loadTaxCategories();
    }, []);

    // Helper to calculate monthly PAYE tax based on annual gross salary
    const calculateMonthlyPAYE = (annualGross) => {
        if (!annualGross || annualGross <= 0) return 0;

        // Locate PAYE category and its active tax rule
        const payeCategory = taxCategories.find(c => c.code === 'PAYE');
        if (!payeCategory || !payeCategory.tax_rules) return 0;

        const activeRule = payeCategory.tax_rules.find(r => r.is_active);
        if (!activeRule || !activeRule.tiers || activeRule.tiers.length === 0) return 0;

        // Sort brackets by tier_order
        const tiers = [...activeRule.tiers].sort((a, b) => a.tier_order - b.tier_order);

        let totalAnnualTax = 0;

        for (const tier of tiers) {
            // const lower = Number(tier.lower_limit) || 0;
            const lower = Math.floor(Number(tier.lower_limit) || 0);
            const upper = tier.upper_limit ? Number(tier.upper_limit) : Infinity;
            const rate = Number(tier.rate) || 0;

            if (annualGross > lower) {
                const taxableInTier = Math.min(annualGross, upper) - lower;
                totalAnnualTax += taxableInTier * rate;
            }
        }

        // Return monthly tax portion
        return totalAnnualTax / 12;
    };

    // 1. Load active stores for logged-in user if storeId is not passed directly
    useEffect(() => {
        const resolveUserStore = async () => {
            if (storeId) {
                setResolvedStoreId(storeId);
                return;
            }
            try {
                const stored = localStorage.getItem('user');
                const parsed = stored ? JSON.parse(stored) : null;
                if (parsed) {
                    const returnedStores = await fetchStoresWhereStaffAssigned(parsed);
                    // console.log(returnedStores)
                    const fetchedStores = returnedStores.filter(store => store.storetype != 'WHS');
                    // console.log(fetchedStores)
                    if (fetchedStores && fetchedStores.length > 0) {
                        const mappedStores = fetchedStores.map(store => ({
                            label: store.identifier,
                            value: store.store_id,
                            currency: store.setccurr,
                        }));
                        setStores(mappedStores);
                        if (fetchedStores.length > 0) {
                            setSelectedStoreId(fetchedStores[0].store_id);
                            setResolvedStoreId(fetchedStores[0].store_id);
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to resolve user assigned store", err);
                showToast(err.response?.data?.detail || "Failed to resolve user assigned store", "error");
            }
        };
        resolveUserStore();
    }, [storeId]);

    // 2. Load employee list
    const loadEmployees = async (storeIdToFetch = resolvedStoreId) => {
        if (!storeIdToFetch) return;

        setLoading(true);
        try {
            const data = await fetchEmployeeList(storeIdToFetch);
            // console.log(data)
            setEmployees(Array.isArray(data) ? data : []);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to retrieve employee profiles.", "error");
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (resolvedStoreId) {
            loadEmployees(resolvedStoreId);
        }
    }, [resolvedStoreId]);

    // 3. Load Banks list for selection dropdown
    const loadBanks = async (storeIdToFetch = resolvedStoreId) => {
        try {
            const banks = await fetchBankList(storeIdToFetch);
            const options = (banks || []).map(b => ({
                label: b.bank_name,
                value: b.bank_code
            }));
            setBankOptions(options);
        } catch (err) {
            showToast("Failed to load bank list.", "error");
        }
    };

    const handleOpenBankModal = (emp) => {
        setSelectedEmployee(emp);

        const existingBankCode = emp.bank_code || emp.bank_account?.bank_code || '';
        const existingAccNum = emp.account_number || emp.bank_account?.account_number || '';
        const existingAccName = emp.account_name || emp.bank_account?.account_name || '';

        setBankCode(existingBankCode);
        setAccountNumber(existingAccNum);

        if (existingAccNum && existingAccName) {
            setVerificationData({
                account_name: existingAccName,
                bank_name: emp.bank_name || emp.bank_account?.bank_name || '',
            });
        } else {
            setVerificationData(null);
        }

        setIsModalOpen(true);
        if (bankOptions.length === 0) {
            loadBanks(resolvedStoreId);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedEmployee(null);
        setVerificationData(null);
    };

    // 4. Verify Account with NUBAN and Bank Code
    const handleVerifyAccount = async (numToVerify = accountNumber) => {
        if (!bankCode) {
            showToast("Please select a bank.", "error");
            return;
        }
        if (!numToVerify || numToVerify.length !== 10 || !/^\d{10}$/.test(numToVerify)) {
            showToast("Account number must be exactly 10 digits.", "error");
            return;
        }

        setVerifying(true);
        setVerificationData(null);
        try {
            const payload = {
                store_id: Number(resolvedStoreId),
                bank_code: bankCode,
                account_number: numToVerify,
            };
            const response = await client.postData('/payroll/verify-bank-with-paystack', payload);
            // console.log(response) // THE VERIFIED ACCOUNT NUMBER COMES IN HERE

            if (response) {
                setVerificationData(response);
                showToast("Account verified successfully!", "success");
            } else {
                showToast("Could not verify account details.", "error");
            }
        } catch (err) {
            showToast(err.response?.data?.detail || "Verification failed. Check account details.", "error");
        } finally {
            setVerifying(false);
        }
    };

    // 5. Submit Verified Bank Details
    const handleSaveBankAccount = async () => {
        if (!verificationData) return;

        setSaving(true);
        try {
            const selectedBank = bankOptions.find(b => b.value === bankCode);
            const payload = {
                member_id: selectedEmployee.users_id,
                bank_code: bankCode,
                bank_name: verificationData.bank_name || selectedBank?.label || '',
                account_number: accountNumber,
                account_name: verificationData.account_name,
                is_active: true,
                is_primary: true,
                is_verified: true,
                is_active: true,
            };

            // console.log(payload)

            await client.postData('/payroll/save-employee-bank-account', payload);
            showToast("Bank account attached successfully!", "success");
            handleCloseModal();
            loadEmployees();
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to save bank account.", "error");
        } finally {
            setSaving(false);
        }
    };

    // handle opening and pre-filling the salary modal
    const handleOpenSalaryModal = (emp) => {
        setSelectedEmployee(emp);
        const salary = emp.salary_structure || emp;

        setSalaryForm({
            basic_salary: salary.basic_salary ?? '',
            housing_allowance: salary.housing_allowance ?? '',
            transport_allowance: salary.transport_allowance ?? '',
            other_allowances: salary.other_allowances ?? '',
            effective_date: salary.effective_date ? new Date(salary.effective_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            effective_until: salary.effective_until ? new Date(salary.effective_until).toISOString().split('T')[0] : '',
            is_active: salary.is_active ?? true
        });

        setIsSalaryModalOpen(true);
    };

    const handleCloseSalaryModal = () => {
        setIsSalaryModalOpen(false);
        setSelectedEmployee(null);
    };

    // Calculate Gross Salary automatically from allowances
    const calculatedGrossSalary = (
        (Number(salaryForm.basic_salary) || 0) +
        (Number(salaryForm.housing_allowance) || 0) +
        (Number(salaryForm.transport_allowance) || 0) +
        (Number(salaryForm.other_allowances) || 0)
    );

    // Convert monthly gross to annual gross for progressive bracket calculation
    const calculatedAnnualGross = calculatedGrossSalary * 12;
    const calculatedMonthlyPAYETax = calculateMonthlyPAYE(calculatedAnnualGross);

    // Save or update salary structure via endpoint
    const handleSaveSalaryStructure = async () => {
        if (!salaryForm.basic_salary || Number(salaryForm.basic_salary) <= 0) {
            showToast("Please enter a valid Basic Salary.", "error");
            return;
        }

        setSavingSalary(true);
        try {
            const payeCategory = taxCategories.find(c => c.code === 'PAYE');
            const activeRule = payeCategory?.tax_rules?.find(r => r.is_active);
            // console.log(activeRule);

            const payload = {
                member_id: selectedEmployee.users_id,
                store_id: resolvedStoreId,
                basic_salary: Number(salaryForm.basic_salary),
                housing_allowance: Number(salaryForm.housing_allowance || 0),
                transport_allowance: Number(salaryForm.transport_allowance || 0),
                other_allowances: Number(salaryForm.other_allowances || 0),
                tax_category_code: payeCategory?.code || 'PAYE',
                tax_rule_id: activeRule?.taxrule_id || null,
                estimated_monthly_paye: calculatedMonthlyPAYETax,
                effective_date: salaryForm.effective_date,
                effective_until: salaryForm.effective_until || null,
                is_active: salaryForm.is_active
            };

            // console.log(payload)

            await client.postData('/payroll/save-salary-structure', payload);
            showToast("Salary structure saved successfully!", "success");
            handleCloseSalaryModal();
            loadEmployees();
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to save salary structure.", "error");
        } finally {
            setSavingSalary(false);
        }
    };

    const filteredEmployees = employees.filter((emp) => {
        const fullName = `${emp.first_name} ${emp.middle_name || ''} ${emp.last_name}`.toLowerCase();
        const search = searchTerm.toLowerCase();
        const bankName = emp.bank_name || emp.bank_account?.bank_name || '';
        const accNumber = emp.account_number || emp.bank_account?.account_number || '';

        return (
            fullName.includes(search) ||
            emp.employeeid?.toLowerCase().includes(search) ||
            emp.email?.toLowerCase().includes(search) ||
            emp.phone?.includes(search) ||
            bankName.toLowerCase().includes(search) ||
            accNumber.includes(search)
        );
    });

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || amount === '') return 'N/A';
        const num = Number(amount);
        if (isNaN(num)) return 'N/A';

        return `${activeCurrencySymbol}${num.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            <Card className="p-6 border-zinc-200 shadow-sm bg-white">
                {/* Header Section */}
                <div className="border-b border-zinc-100 pb-4 mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                            <Users size={20} className="text-blue-600" /> Employee Directory
                        </h2>
                        <p className="text-xs text-zinc-500">View detailed profiles, contact info, bank details, and compensation structures.</p>
                    </div>

                    {/* MODIFICATION: Branch Selector & Refresh Button Container */}
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <select
                            value={selectedStoreId}
                            onChange={(e) => {
                                const newId = e.target.value;
                                setSelectedStoreId(newId);
                                setResolvedStoreId(newId);
                                loadEmployees(newId);
                            }}
                            className="bg-zinc-50 border border-zinc-200 text-zinc-700 text-xs rounded-lg px-2.5 py-2 focus:ring-2 focus:ring-blue-600 focus:outline-none transition-all min-w-[140px]"
                        >
                            {stores.map((s) => (
                                <option key={s.value} value={s.value}>
                                    {s.label}
                                </option>
                            ))}
                        </select>

                        <Button
                            onClick={() => loadEmployees(resolvedStoreId)}
                            disabled={loading}
                            className="bg-zinc-50 hover:bg-zinc-100 text-zinc-700 text-xs px-3 py-2 border border-zinc-200 flex items-center gap-1.5 whitespace-nowrap"
                        >
                            <RefreshCw size={14} className={loading ? 'animate-spin text-blue-600' : ''} />
                            Refresh
                        </Button>

                        {/* <Button
                            onClick={() => navigate('/wallet/payroll-maker')}
                            disabled={loading}
                            className="bg-zinc-50 hover:bg-zinc-100 text-zinc-700 text-xs px-3 py-2 border border-zinc-200 flex items-center gap-1.5 whitepace-nowrap"
                        >
                            <LogOut size={14} className={loading ? 'animate-spin text-blue-600' : ''} />
                            Payroll Maker
                        </Button> */}
                        <Dropdown
                            align="right"
                            trigger={
                                <Button
                                    disabled={loading}
                                    className="bg-zinc-50 hover:bg-zinc-100 text-zinc-700 text-xs px-3 py-2 border border-zinc-200 flex items-center gap-1.5 whitespace-nowrap"
                                >
                                    <LogOut size={14} className={loading ? 'animate-spin text-blue-600' : ''} />
                                    Payroll Actions
                                    <ChevronDown size={12} className="text-zinc-500" />
                                </Button>
                            }
                        >
                            <div className="py-1">
                                {/* Payroll Maker Option */}
                                <button
                                    onClick={() => navigate('/wallet/payroll-maker')}
                                    className="w-full text-left px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 flex items-center gap-2 transition-colors"
                                >
                                    <LogOut size={14} className="text-blue-600" />
                                    <span>Payroll Maker</span>
                                </button>

                                {/* Payroll Checker Option */}
                                <button
                                    onClick={() => navigate('/wallet/payroll-checker')}
                                    className="w-full text-left px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 flex items-center gap-2 transition-colors border-t border-zinc-100"
                                >
                                    <CheckSquare size={14} className="text-emerald-600" />
                                    <span>Payroll Checker</span>
                                </button>
                            </div>
                        </Dropdown>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="mb-6 max-w-md mx-auto relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input
                        type="text"
                        placeholder="Search name, ID, email, bank details..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-600 focus:outline-none transition-all"
                    />
                </div>

                {/* Profiles Grid */}
                {loading ? (
                    <div className="p-12 text-center text-zinc-500 text-xs">
                        <Loader2 className="animate-spin inline mr-2 text-blue-600" size={18} /> Loading employee records...
                    </div>
                ) : filteredEmployees.length === 0 ? (
                    <div className="p-12 border border-dashed border-zinc-200 rounded-xl text-center text-zinc-400 text-xs">
                        No employee profiles found matching your search.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredEmployees.map((emp) => {
                            const fullName = [emp.first_name, emp.middle_name, emp.last_name].filter(Boolean).join(' ');
                            const initials = `${emp.first_name?.[0] || ''}${emp.last_name?.[0] || ''}`.toUpperCase();

                            const bank = emp.bank_account || {
                                bank_name: emp.bank_name,
                                account_number: emp.account_number,
                                account_name: emp.account_name,
                            };
                            // const salary = emp.salary_structure || {
                            //     gross_salary: emp.gross_salary,
                            //     basic_salary: emp.basic_salary,
                            // };
                            const salary = emp.salary_structure || emp;

                            const hasBankInfo = Boolean(bank.bank_name || bank.account_number);
                            const hasSalaryInfo = Boolean(salary.gross_salary || salary.basic_salary);

                            return (
                                <div
                                    key={emp.users_id}
                                    className="p-4 border border-zinc-200 rounded-xl bg-white hover:border-blue-200 hover:shadow-sm transition-all flex flex-col justify-between space-y-3"
                                >
                                    <div className="space-y-3">
                                        {/* Avatar & Basic Details */}
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm flex-shrink-0">
                                                {initials || <UserCheck size={18} />}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h4 className="text-sm font-bold text-zinc-900 truncate">{fullName}</h4>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className="text-[10px] font-mono bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded font-semibold">
                                                        ID: {emp.employeeid}
                                                    </span>
                                                    {emp.employeetype && (
                                                        <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium border border-blue-100 capitalize">
                                                            {emp.employeetype}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Contact Information */}
                                        <div className="space-y-1.5 pt-2 border-t border-zinc-100 text-xs text-zinc-600">
                                            <div className="flex items-center gap-2 truncate">
                                                <Mail size={13} className="text-zinc-400 flex-shrink-0" />
                                                <span className="truncate">{emp.email || 'No email provided'}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Phone size={13} className="text-zinc-400 flex-shrink-0" />
                                                <span>{emp.phone || 'No phone number'}</span>
                                            </div>
                                        </div>

                                        {/* Banking Information Section */}
                                        <div className="pt-2 border-t border-zinc-100 text-xs text-zinc-600">
                                            <div className="flex items-center justify-between font-semibold text-zinc-700 mb-1">
                                                <span className="flex items-center gap-1.5 text-[11px]">
                                                    <Building2 size={13} className="text-zinc-500" /> Banking Details
                                                </span>
                                                {emp.employeetype !== 'EXTERNAL' && (
                                                    <button
                                                        onClick={() => handleOpenBankModal(emp)}
                                                        className="text-blue-600 hover:text-blue-700 text-[10px] flex items-center gap-0.5 font-medium hover:underline"
                                                    >
                                                        <Plus size={11} /> {hasBankInfo ? 'Edit' : 'Add Bank'}
                                                    </button>
                                                )}
                                            </div>
                                            {hasBankInfo ? (
                                                <div className="bg-zinc-50 p-2 rounded-lg space-y-1 text-[11px] font-mono border border-zinc-100">
                                                    <div className="flex justify-between">
                                                        <span className="text-zinc-400 font-sans">Bank:</span>
                                                        <span className="font-medium text-zinc-800">{bank.bank_name}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-zinc-400 font-sans">Account #:</span>
                                                        <span className="font-medium text-zinc-800">{bank.account_number}</span>
                                                    </div>
                                                    {bank.account_name && (
                                                        <div className="flex justify-between">
                                                            <span className="text-zinc-400 font-sans">Name:</span>
                                                            <span className="font-medium text-zinc-800 truncate max-w-[120px]" title={bank.account_name}>
                                                                {bank.account_name}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-[11px] text-zinc-400 italic">No bank account linked</p>
                                            )}
                                        </div>

                                        {/* Compensation Section */}
                                        <div className="pt-2 border-t border-zinc-100 text-xs text-zinc-600">
                                            <div className="flex items-center justify-between font-semibold text-zinc-700 mb-1">
                                                <span className="flex items-center gap-1.5 text-[11px]">
                                                    <Wallet size={13} className="text-emerald-600" /> Compensation
                                                </span>
                                                {emp.employeetype !== 'EXTERNAL' && (
                                                    <button
                                                        onClick={() => handleOpenSalaryModal(emp)}
                                                        className="text-emerald-600 hover:text-emerald-700 text-[10px] flex items-center gap-0.5 font-medium hover:underline"
                                                    >
                                                        {hasSalaryInfo ? <Edit3 size={11} /> : <Plus size={11} />}
                                                        {hasSalaryInfo ? 'Edit Salary' : 'Add Salary'}
                                                    </button>
                                                )}
                                            </div>
                                            {hasSalaryInfo ? (
                                                <div className="bg-emerald-50/50 p-2 rounded-lg border border-emerald-100/60 space-y-1 text-[11px]">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-zinc-500">Gross Salary:</span>
                                                        <span className="font-bold text-emerald-700">
                                                            {formatCurrency(salary.gross_salary) || 'N/A'}
                                                        </span>
                                                    </div>
                                                    {salary.basic_salary && (
                                                        <div className="flex justify-between items-center text-[10px]">
                                                            <span className="text-zinc-400">Basic Pay:</span>
                                                            <span className="text-zinc-600 font-medium">
                                                                {formatCurrency(salary.basic_salary)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-[11px] text-zinc-400 italic">No salary structure configured</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="pt-3 border-t border-zinc-100 flex justify-between items-center text-[10px] text-zinc-400 font-mono">
                                        <span>User ID: #{emp.users_id}</span>
                                        <span className="flex items-center gap-1 text-emerald-600 font-sans font-medium">
                                            <Shield size={10} /> Active
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* Bank Account Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={`Bank Account: ${selectedEmployee ? `${selectedEmployee.first_name} ${selectedEmployee?.middle_name} ${selectedEmployee.last_name}` : ''}`}
                size="md"
            >
                <div className="space-y-4">
                    <SearchableSelectBox
                        name="bank_code"
                        label="Select Bank"
                        value={bankCode}
                        onChange={(e) => {
                            setBankCode(e.target.value);
                            setVerificationData(null);
                        }}
                        options={bankOptions}
                        required
                        placeholder="Search & select bank..."
                    />

                    <Input
                        name="account_number"
                        label="NUBAN Account Number"
                        placeholder="10-digit account number"
                        value={accountNumber}
                        maxLength={10}
                        onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setAccountNumber(val);
                            setVerificationData(null);

                            // auto-trigger verification upon reaching 10 digits
                            if (val.length === 10) {
                                handleVerifyAccount(val);
                            }
                        }}
                        required
                    />

                    {/* Display loading State or Returned Account Name Input Field */}
                    {verifying && (
                        <div className="flex items-center gap-2 text-xs text-blue-600">
                            <Loader2 size={14} className="animate-spin" /> Verifying account details...
                        </div>
                    )}

                    {verificationData && (
                        <Input
                            name="account_name"
                            label="Account Name"
                            value={verificationData.account_name || ''}
                            readOnly
                            className="bg-zinc-100 cursor-not-allowed"
                        />
                    )}

                    {/* Action Buttons */}
                    <div className="pt-4 flex justify-end gap-2 border-t border-zinc-100">
                        <Button variant="ghost" size="sm" onClick={handleCloseModal}>
                            Cancel
                        </Button>

                        <Button
                            variant="indigo"
                            size="sm"
                            onClick={handleSaveBankAccount}
                            disabled={saving || !verificationData}
                            className="flex items-center gap-1.5"
                        >
                            {saving && <Loader2 size={14} className="animate-spin" />}
                            Save
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Salary Structure Modal */}
            <Modal
                isOpen={isSalaryModalOpen}
                onClose={handleCloseSalaryModal}
                title={`Salary Structure: ${selectedEmployee ? `${selectedEmployee.first_name} ${selectedEmployee?.middle_name || ''} ${selectedEmployee.last_name}` : ''}`}
                size="md"
            >
                <div className="space-y-4">
                    <Input
                        name="basic_salary"
                        label={`Basic Salary (${activeCurrencySymbol})`}
                        type="number"
                        placeholder={`${activeCurrencySymbol}0.00`}
                        value={salaryForm.basic_salary}
                        onChange={(e) => setSalaryForm({ ...salaryForm, basic_salary: e.target.value })}
                        required
                    />

                    <div className="grid grid-cols-3 gap-2">
                        <Input
                            name="housing_allowance"
                            label={`Housing (${activeCurrencySymbol})`}
                            type="number"
                            placeholder={`${activeCurrencySymbol}0.00`}
                            value={salaryForm.housing_allowance}
                            onChange={(e) => setSalaryForm({ ...salaryForm, housing_allowance: e.target.value })}
                        />
                        <Input
                            name="transport_allowance"
                            label={`Transport (${activeCurrencySymbol})`}
                            type="number"
                            placeholder={`${activeCurrencySymbol}0.00`}
                            value={salaryForm.transport_allowance}
                            onChange={(e) => setSalaryForm({ ...salaryForm, transport_allowance: e.target.value })}
                        />
                        <Input
                            name="other_allowances"
                            label={`Other (${activeCurrencySymbol})`}
                            type="number"
                            placeholder={`${activeCurrencySymbol}0.00`}
                            value={salaryForm.other_allowances}
                            onChange={(e) => setSalaryForm({ ...salaryForm, other_allowances: e.target.value })}
                        />
                    </div>

                    {/* Auto-Calculated Gross Salary Breakdown */}
                    <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200/60 text-xs flex justify-between items-center">
                        <span className="font-semibold text-emerald-900">Calculated Gross Salary:</span>
                        <span className="font-bold text-emerald-700 text-sm">
                            {formatCurrency(calculatedGrossSalary)}
                        </span>
                    </div>

                    {/* NEW: Auto-Calculated Estimated PAYE Tax */}
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200/60 text-xs flex justify-between items-center">
                        <div>
                            <span className="font-semibold text-blue-900 block">Estimated Monthly PAYE Tax:</span>
                            <span className="text-[10px] text-blue-600">Based on progressive brackets</span>
                        </div>
                        <span className="font-bold text-blue-700 text-sm">
                            {formatCurrency(calculatedMonthlyPAYETax)}
                        </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Input
                            name="effective_date"
                            label="Effective Start Date"
                            type="date"
                            value={salaryForm.effective_date}
                            onChange={(e) => setSalaryForm({ ...salaryForm, effective_date: e.target.value })}
                            required
                        />
                        <Input
                            name="effective_until"
                            label="Effective End Date (Optional)"
                            type="date"
                            value={salaryForm.effective_until}
                            onChange={(e) => setSalaryForm({ ...salaryForm, effective_until: e.target.value })}
                        />
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                        <input
                            type="checkbox"
                            id="is_active"
                            checked={salaryForm.is_active}
                            onChange={(e) => setSalaryForm({ ...salaryForm, is_active: e.target.checked })}
                            className="rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <label htmlFor="is_active" className="text-xs text-zinc-700 font-medium cursor-pointer">
                            Mark structure as active
                        </label>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-4 flex justify-end gap-2 border-t border-zinc-100">
                        <Button variant="ghost" size="sm" onClick={handleCloseSalaryModal}>
                            Cancel
                        </Button>

                        <Button
                            variant="indigo"
                            size="sm"
                            onClick={handleSaveSalaryStructure}
                            disabled={savingSalary}
                            className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            {savingSalary && <Loader2 size={14} className="animate-spin" />}
                            Save Structure
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default EmployeeProfilesManager;